import {Component, OnDestroy, OnInit} from '@angular/core';
import {StorageService, StorageType} from 'gast-common-ng';
import {BsLocaleService} from 'ngx-bootstrap';
import {NgxSpinnerService} from 'ngx-spinner';
import {Subscription} from 'rxjs';
import {TestdafDigitalResultDto} from '../shared/dto/testdaf-digital-result-dto';
import {VerificationTestdafFormDto} from '../shared/dto/verification-testdaf-form.dto';
import {CertificateService} from '../shared/services/certificate.service';
import {DateStringUtil} from '../shared/utils/date-string-util';

export class TdnModel {
  displayText: string;
  value: string;
}

export class ScoreModel {
  displayText: string;
  value: string;
}

@Component({
  selector: 'app-verification-testdaf-form',
  templateUrl: './verification-testdaf-form.component.html',
  styleUrls: ['./verification-testdaf-form.component.css']
})
export class VerificationTestdafFormComponent implements OnInit, OnDestroy {
  result: TestdafDigitalResultDto = new TestdafDigitalResultDto();
  isSubmitted = false;
  hasError = false;
  isLoading = false;
  selectedExamType = 'PAPERBASED_TESTDAF';

  tdnValues = [];
  scores = [];

  // inputs
  participantNumber: string;
  participantName: string;
  dateOfBirth: Date;
  examDate: Date;
  readingTdn: string;
  listeningTdn: string;
  writingTdn: string;
  speakingTdn: string;
  readingScore: string;
  listeningScore: string;
  writingScore: string;
  speakingScore: string;

  subscription = new Subscription();

  constructor(
    private localeService: BsLocaleService,
    private certificationService: CertificateService,
    private spinnerService: NgxSpinnerService,
    private storageService: StorageService) {
  }

  ngOnInit() {
    // init datepicker locale
    this.localeService.use('de');

    this.generateTdnValues();
    this.generateScores();

    // subscribe to digital result verification
    this.subscription.add(this.certificationService.testdafDigitalResultLoaded.subscribe(result => {
      this.spinnerService.hide();
      if (result) {
        this.result = result;
      } else {
        this.result.verified = false;
      }
      this.isLoading = false;
    }));

    this.subscription.add(this.certificationService.verificationError.subscribe(() => {
      this.spinnerService.hide();
      this.result.verified = false;
      this.isLoading = false;
    }));

    // restore inputs
    this.restoreInputs();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /** set config for date picker */
  getBsConfig() {
    return {containerClass: 'theme-blue'};
  }

  submit(): void {
    this.isSubmitted = true;
    if (this.selectedExamType === 'PAPERBASED_TESTDAF') {
      this.logPaperbasedTestDaF();
      if (this.participantNumber && this.participantName && this.dateOfBirth && this.examDate
        && this.readingTdn && this.listeningTdn && this.writingTdn && this.speakingTdn) {
        const dto = new VerificationTestdafFormDto();
        dto.participantNumber = this.participantNumber;
        dto.participantName = this.participantName;
        dto.dateOfBirth = DateStringUtil.getDateString(this.dateOfBirth);
        dto.examDate = DateStringUtil.getDateString(this.examDate);
        dto.readingTdn = this.readingTdn;
        dto.listeningTdn = this.listeningTdn;
        dto.writingTdn = this.writingTdn;
        dto.speakingTdn = this.speakingTdn;
        this.certificationService.getTestdafPaperbasedFormResults(dto);
        this.storageService.setItem('VERIFICATION_FORM', dto, StorageType.Local);
        this.spinnerService.show();
        this.isLoading = true;
        this.hasError = false;
      } else {
        this.hasError = true;
      }
    } else if (this.selectedExamType === 'DIGITAL_TESTDAF') {
      this.logDigitalTestDaF();
      if (this.participantNumber && this.participantName && this.dateOfBirth && this.examDate
        && this.readingTdn && this.listeningTdn && this.writingTdn && this.speakingTdn
        && this.readingScore && this.listeningScore && this.writingScore && this.speakingScore) {
        const dto = new VerificationTestdafFormDto();
        dto.participantNumber = this.participantNumber;
        dto.participantName = this.participantName;
        dto.dateOfBirth = DateStringUtil.getDateString(this.dateOfBirth);
        dto.examDate = DateStringUtil.getDateString(this.examDate);
        dto.readingTdn = this.readingTdn;
        dto.listeningTdn = this.listeningTdn;
        dto.writingTdn = this.writingTdn;
        dto.speakingTdn = this.speakingTdn;
        dto.readingScore = this.readingScore;
        dto.listeningScore = this.listeningScore;
        dto.writingScore = this.writingScore;
        dto.speakingScore = this.speakingScore;
        this.certificationService.getTestdafDigitalFormResults(dto);
        this.storageService.setItem('VERIFICATION_FORM', dto, StorageType.Local);
        this.spinnerService.show();
        this.isLoading = true;
        this.hasError = false;
      } else {
        this.hasError = true;
      }
    }
  }

  generateTdnValues() {
    const tdnValue1 = new TdnModel();
    tdnValue1.displayText = 'TDN u3';
    tdnValue1.value = 'u3';
    const tdnValue2 = new TdnModel();
    tdnValue2.displayText = 'TDN 3';
    tdnValue2.value = '3';
    const tdnValue3 = new TdnModel();
    tdnValue3.displayText = 'TDN 4';
    tdnValue3.value = '4';
    const tdnValue4 = new TdnModel();
    tdnValue4.displayText = 'TDN 5';
    tdnValue4.value = '5';
    this.tdnValues.push(tdnValue1);
    this.tdnValues.push(tdnValue2);
    this.tdnValues.push(tdnValue3);
    this.tdnValues.push(tdnValue4);
  }

  generateScores() {
    for (let i = 1; i < 21; i++) {
      const score = new ScoreModel();
      score.displayText = i + '/20';
      score.value = i.toString();
      this.scores.push(score);
    }
  }

  logDigitalTestDaF(): void {
    console.log('participant number: ' + this.participantNumber);
    console.log('participant name: ' + this.participantName);
    console.log('date of birth: ' + DateStringUtil.getDateString(this.dateOfBirth));
    console.log('exam date: ' + DateStringUtil.getDateString(this.examDate));
    console.log('readingTdn: ' + this.readingTdn);
    console.log('listeningTdn: ' + this.listeningTdn);
    console.log('writingTdn: ' + this.writingTdn);
    console.log('speakingTdn: ' + this.speakingTdn);
    console.log('readingScore: ' + this.readingScore);
    console.log('listeningScore: ' + this.listeningScore);
    console.log('writingScore: ' + this.writingScore);
    console.log('speakingScore: ' + this.speakingScore);
  }

  logPaperbasedTestDaF(): void {
    console.log('participant number: ' + this.participantNumber);
    console.log('participant name: ' + this.participantName);
    console.log('date of birth: ' + DateStringUtil.getDateString(this.dateOfBirth));
    console.log('exam date: ' + DateStringUtil.getDateString(this.examDate));
    console.log('readingTdn: ' + this.readingTdn);
    console.log('listeningTdn: ' + this.listeningTdn);
    console.log('writingTdn: ' + this.writingTdn);
    console.log('speakingTdn: ' + this.speakingTdn);
  }

  private restoreInputs() {
    const dto = this.storageService.getItem('VERIFICATION_FORM', StorageType.Local);
    if (dto) {
      this.participantNumber = dto.participantNumber;
      this.participantName = dto.participantName;
      this.dateOfBirth = new Date(dto.dateOfBirth);
      this.examDate = new Date(dto.examDate);
      this.readingTdn = dto.readingTdn;
      this.listeningTdn = dto.listeningTdn;
      this.writingTdn = dto.writingTdn;
      this.speakingTdn = dto.speakingTdn;
      this.readingScore = dto.readingScore;
      this.listeningScore = dto.listeningScore;
      this.writingScore = dto.writingScore;
      this.speakingScore = dto.speakingScore;
    }
  }
}
